import { levelAt, worldToRadar, type Level, type MapId } from "@69ly/maps";
import { frameAtTick, PF, type Replay } from "@69ly/shared";
import { indexRound } from "./scene.ts";

/** Where players were (or died, or killed) over the chosen rounds, as a grid over the radar. */
export interface HeatFilter {
  what: "presence" | "deaths" | "kills";
  rounds: "this" | "all" | "first" | "second";
  side: "both" | "t" | "ct";
  /** A player's index, or null for everyone. */
  player: number | null;
}

export const HEAT_SIZE = 128;

/** The rounds a filter covers: this one, all, or a half (the halves split where sides swap first). */
export function roundsOf(r: Replay, filter: HeatFilter, current: number): number[] {
  const all = r.rounds.map((_, i) => i);
  if (filter.rounds === "this") return [current];
  if (filter.rounds === "all") return all;
  const swap = r.rounds.findIndex((x, i) => i > 0 && x.tTeam !== r.rounds[i - 1].tTeam);
  const cut = swap < 0 ? r.rounds.length : swap;
  return filter.rounds === "first" ? all.slice(0, cut) : all.slice(cut);
}

/**
 * The grid: HEAT_SIZE × HEAT_SIZE values from 0 to 1, blurred. Positions are counted once
 * per frame (16 a second) while the round is live; deaths and kills once each, spread wider.
 */
export function heatGrid(r: Replay, map: MapId, filter: HeatFilter, current: number, level: Level | null): { grid: Float32Array; samples: number } {
  const N = HEAT_SIZE;
  const raw = new Float32Array(N * N);
  let samples = 0;
  const add = (x: number, y: number, z: number, weight = 1) => {
    if (level && levelAt(map, z) !== level) return;
    const p = worldToRadar(map, x, y);
    const cx = Math.floor(p.u * N);
    const cy = Math.floor(p.v * N);
    if (cx < 0 || cy < 0 || cx >= N || cy >= N) return;
    raw[cy * N + cx] += weight;
    samples++;
  };
  const P = r.players.length;
  const c = r.columns;
  for (const ri of roundsOf(r, filter, current)) {
    const round = r.rounds[ri];
    const sideOk = (team: number) => filter.side === "both" || (filter.side === "t") === (team === round.tTeam);
    if (filter.what === "presence") {
      for (let f = round.frameFrom; f < round.frameTo; f++) {
        const tick = c.frameTicks[f];
        if (tick < round.liveTick || tick > round.endTick) continue;
        for (let p = 0; p < P; p++) {
          if (filter.player !== null && p !== filter.player) continue;
          if (!sideOk(r.players[p].team)) continue;
          const at = f * P + p;
          if ((c.flags[at] & (PF.present | PF.alive)) !== (PF.present | PF.alive)) continue;
          add(c.x[at], c.y[at], c.z[at]);
        }
      }
    } else {
      const ix = indexRound(r, ri);
      for (const k of ix.kills) {
        if (filter.what === "deaths") {
          if (k.victim < 0 || (filter.player !== null && k.victim !== filter.player) || !sideOk(r.players[k.victim].team)) continue;
          const d = ix.deaths.find((x) => x.tick === k.tick && x.victim === k.victim);
          if (d) add(d.x, d.y, d.z, 1);
        } else {
          if (k.killer < 0 || k.killer === k.victim || (filter.player !== null && k.killer !== filter.player) || !sideOk(r.players[k.killer].team)) continue;
          const f = frameAtTick(c.frameTicks, round.frameFrom, round.frameTo, k.tick);
          const at = f * P + k.killer;
          if (c.flags[at] & PF.present) add(c.x[at], c.y[at], c.z[at], 1);
        }
      }
    }
  }
  const grid = blur(raw, N, filter.what === "presence" ? 2 : 4);
  // Scaled to a high percentile, so one corner where everyone waits does not wash out the rest.
  const values = Array.from(grid).filter((v) => v > 0).sort((a, b) => a - b);
  const top = values.length ? values[Math.floor(values.length * 0.985)] || values[values.length - 1] : 1;
  for (let i = 0; i < grid.length; i++) grid[i] = Math.min(1, grid[i] / top);
  return { grid, samples };
}

/** Three box blurs make a near-Gaussian one. */
function blur(src: Float32Array<ArrayBuffer>, n: number, radius: number): Float32Array<ArrayBuffer> {
  let a = src;
  let b: Float32Array<ArrayBuffer> = new Float32Array(src.length);
  for (let pass = 0; pass < 3; pass++) {
    for (let y = 0; y < n; y++) {
      let sum = 0;
      for (let x = -radius; x <= radius; x++) sum += a[y * n + Math.min(n - 1, Math.max(0, x))];
      for (let x = 0; x < n; x++) {
        b[y * n + x] = sum / (2 * radius + 1);
        sum += a[y * n + Math.min(n - 1, x + radius + 1)] - a[y * n + Math.max(0, x - radius)];
      }
    }
    [a, b] = [b, a];
    for (let x = 0; x < n; x++) {
      let sum = 0;
      for (let y = -radius; y <= radius; y++) sum += a[Math.min(n - 1, Math.max(0, y)) * n + x];
      for (let y = 0; y < n; y++) {
        b[y * n + x] = sum / (2 * radius + 1);
        sum += a[Math.min(n - 1, y + radius + 1) * n + x] - a[Math.max(0, y - radius) * n + x];
      }
    }
    [a, b] = [b, a];
  }
  return a === src ? new Float32Array(a) : a;
}

/** Transparent → cyan → amber → red, for a value from 0 to 1. */
export function heatColor(v: number): [number, number, number, number] {
  if (v <= 0.02) return [0, 0, 0, 0];
  const stops: [number, [number, number, number]][] = [
    [0, [47, 212, 232]],
    [0.5, [244, 184, 96]],
    [1, [255, 70, 70]],
  ];
  const i = v < 0.5 ? 0 : 1;
  const [a, ca] = stops[i];
  const [b, cb] = stops[i + 1];
  const k = (v - a) / (b - a);
  const mix = (j: number) => Math.round(ca[j] + (cb[j] - ca[j]) * k);
  return [mix(0), mix(1), mix(2), Math.round(255 * Math.min(0.85, 0.25 + v * 0.7))];
}
